import * as React from 'react';
import styled from 'styled-components';
import { countLatestItems, cssClampValue } from '../utils/common';
import PostCardAlt from './PostCardAlt';
import ScrollableRow from './ScrollableRow';
import { LatestItemCount, LatestItemTitle } from './styled-components';
import ViewAllLink from './ViewAllLink';
import { pages } from '../../config';

const postWidth = 'min(100vw, 300px)';
const postBackgroundPadding = '20px';

const Wrapper = styled.div`
  margin: clamp(80px, 13vw, 120px) 0;
`;

const LatestPostScrollableRow = styled(ScrollableRow)`
  &__scrollable {
    padding: ${postBackgroundPadding}; // space for hover effect
    gap: ${cssClampValue(20, 70)};
  }

  @media (min-width: 992px) {
    button {
      top: calc(${postWidth} / 2 + ${postBackgroundPadding});
    }
  }
`;

const LatestPostCard = styled(PostCardAlt)`
  flex: 0 0 ${postWidth};
`;

interface Props {
  title?: string;
  posts: readonly Queries.PostCardDataFragment[];
  totalCount?: number;
  className?: string;
}

export default function LatestPosts({
  title = 'Latest guides and essays',
  posts,
  totalCount = 0,
  className = '',
}: Props) {
  const newItemCount = React.useMemo(() => countLatestItems(posts), [posts]);

  const latestItemText = React.useMemo(() => {
    if (newItemCount === 0) {
      return '';
    }
    if (newItemCount === 1) {
      return `${newItemCount} more post has just been published.`;
    }
    return `${newItemCount} more posts have just been published.`;
  }, [newItemCount]);

  if (!posts) {
    return null;
  }

  return (
    <Wrapper className={className}>
      <LatestItemTitle className={`${className}__title`}>{title}</LatestItemTitle>
      <LatestItemCount>{latestItemText}</LatestItemCount>
      <LatestPostScrollableRow title={title}>
        {posts.map((post) => (
          <LatestPostCard key={post.id} data={post} />
        ))}
      </LatestPostScrollableRow>
      <ViewAllLink link={pages.blog}>{`View all ${totalCount} posts`}</ViewAllLink>
    </Wrapper>
  );
}
